import {Attributes, AttributesRow, Card, DataStatus} from 'platform/components';
import {Box, Heading, Link, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

// eslint-disable-next-line no-restricted-imports -- Intended
import {mergeAll, path} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {RequestItem} from '@dms/api/shared';
import {useGetServiceOrderReturnQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {customerRoutes, vehiclesRoutes, warehouseRoutes, workshopRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  getCustomerName,
  logError,
  queryParams,
} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ServiceOrderReturnsMaterialReturnListProps extends RequiredTestIdProps {
  serviceOrderReturnId: string;
  onAddToBasket: (selectedItems: RequestItem[]) => Promise<void>;
}

type ServiceOrderMaterialReturnItemRow = {
  id: string;
  articleId: {value: string};
  warehouseId: {value: string};
  availableForReturn: {value: number};
  unit: {value: {key: string}};
  priceUnitWithoutVat: {value: number};
  priceUnitWithVat: {value: number};
};

export function ServiceOrderReturnMaterialList(props: ServiceOrderReturnsMaterialReturnListProps) {
  const serviceOrderReturnQuery = useGetServiceOrderReturnQuery({
    serviceOrderReturnId: props.serviceOrderReturnId,
  });

  const vehicleQuery = useGetVehicleQuery(
    {vehicleId: serviceOrderReturnQuery.data?.serviceOrder.vehicleId as string},
    {skip: isNilOrEmpty(serviceOrderReturnQuery.data?.serviceOrder.vehicleId)}
  );

  const customerQuery = useGetCustomerV2Query(
    {customerId: serviceOrderReturnQuery.data?.serviceOrder.customerId as string},
    {skip: isNilOrEmpty(serviceOrderReturnQuery.data?.serviceOrder.customerId)}
  );

  const serviceCaseId = serviceOrderReturnQuery.data?.serviceOrderReturn.serviceCaseId;

  const serviceOrderId = serviceOrderReturnQuery.data?.serviceOrderReturn.serviceOrderId;

  const customerId = serviceOrderReturnQuery.data?.serviceOrder.customerId;

  const vehicleId = serviceOrderReturnQuery.data?.serviceOrder.vehicleId;

  const customerName = getCustomerName(customerQuery.data);

  const isLoading =
    serviceOrderReturnQuery.isLoading || vehicleQuery.isLoading || customerQuery.isLoading;

  const hasError = serviceOrderReturnQuery.isError || vehicleQuery.isError || customerQuery.isError;

  const actionCallback: ActionCallback = ({actionKey, rowData, refreshData, deselectAll}) => {
    match(actionKey)
      .with('add', () => {
        const castedRowData = (
          isArray(rowData) ? rowData : [rowData]
        ) as ServiceOrderMaterialReturnItemRow[];

        const requestPayload: RequestItem[] = [];

        castedRowData.forEach((row) => {
          requestPayload.push({
            originEntityId: row.id,
            articleId: row.articleId.value,
            quantity: row.availableForReturn.value,
            handlingUnit: row.unit.value.key,
            priceUnitWithoutVat: row.priceUnitWithoutVat.value,
            priceUnitWithVat: row.priceUnitWithVat.value,
          });
        });

        return props
          .onAddToBasket(requestPayload)
          .then(deselectAll)
          .then(() => refreshData());
      })
      .with('details', () => {
        const articleId = path(['articleId', 'value'], rowData) as string;
        const warehouseId = path(['warehouseId', 'value'], rowData) as string;

        if (isNilOrEmpty(articleId)) {
          return logError('articleId is not defined');
        }

        if (isNilOrEmpty(warehouseId)) {
          return logError('warehouseId is not defined');
        }

        return window.open(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {
              id: articleId,
              warehouseId,
            },
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceOrderId}]),
    [serviceOrderId]
  );

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.number'),
      content:
        serviceCaseId && serviceOrderId ? (
          <Link
            title={serviceOrderReturnQuery.data?.serviceOrder.number}
            rightIcon="action/open_in_new"
            size="small"
            href={composePath(workshopRoutes.serviceCaseDetail, {
              params: {id: serviceCaseId},
              queryParams: {
                [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrderId,
              },
            })}
            target="_blank"
          />
        ) : (
          serviceOrderReturnQuery.data?.serviceOrder.number
        ),
    },
    {
      label: i18n.t('entity.warehouse.labels.orderType'),
      value: serviceOrderReturnQuery.data?.serviceOrder.type,
    },
    {
      label: i18n.t('entity.order.labels.orderVariant'),
      value: serviceOrderReturnQuery.data?.serviceOrder.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      content: customerId ? (
        <Link
          title={customerName}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: customerId},
          })}
          target="_blank"
        />
      ) : (
        customerName
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: vehicleId ? (
        <Link
          title={vehicleQuery.data?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: vehicleId},
          })}
          target="_blank"
        />
      ) : (
        vehicleQuery.data?.title
      ),
    },
    {
      label: i18n.t('entity.vehicle.labels.licensePlate'),
      value: vehicleQuery.data?.vehicle?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: vehicleQuery.data?.vehicle?.vin,
    },
  ];

  return (
    <Card
      isFullHeight
      title={i18n.t('entity.warehouse.labels.serviceOrder')}
      data-testid={props['data-testid']}
    >
      <DataStatus isLoading={isLoading} isError={hasError} minHeight="100%">
        <VStack spacing={4} height="100%">
          <Card variant="inlineGrey">
            <Attributes
              rows={rows}
              size="quarter"
              data-testid={suffixTestId('attributes', props)}
            />
          </Card>
          <Box flex={1}>
            <Heading size={5}>{i18n.t('entity.warehouse.labels.addedSpareParts')}</Heading>
            <DmsDataGrid
              gridCode="warehouse-service-order-return-select-items"
              actionCallback={actionCallback}
              queryModifier={queryModifier}
              data-testid="warehouse-service-order-return-select-items"
            />
          </Box>
        </VStack>
      </DataStatus>
    </Card>
  );
}
